import axios from 'axios';
import { reportWarning } from './error-logging';
import { getRoutingKey } from './routing-key';
import { shouldRedirectToLoginForResponse } from './session-auth-http';
import { redirectToLoginOnce } from './session-auth-redirect';

const CONSECUTIVE_DIGITS = /[\d]{2,}/gi;

export const httpClient = axios.create({
  baseURL: '/',
  xsrfCookieName: 'studios_csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

httpClient.interceptors.request.use(config => {
  if (config.url?.startsWith('/api/')) {
    config.headers['X-CP-Routing-Key'] = getRoutingKey();
  }
  return config;
});

let hasInternalError = false;
let failedRequests = [];

httpClient.interceptors.response.use(
  response => {
    hasInternalError = false;
    failedRequests = [];
    return response;
  },
  error => {
    const { response, config = {} } = error;
    const {
      url = '',
      method,
      logErrorToDatadog: shouldLogErrorToDatadog = () => true,
      ignoreInternalError = false,
    } = config;
    let statusString = null;

    if (response) {
      const { status } = response;
      statusString = status.toString();

      if (statusString[0] === '5' || statusString[0] === '4') {
        const normalizedUrl = url.replace(CONSECUTIVE_DIGITS, 'xxx');

        if (shouldLogErrorToDatadog(status)) {
          reportWarning(`${statusString} ${method.toUpperCase()} ${normalizedUrl}`, { url });
        }
      }

      // Session expired or forbidden on partner session APIs → login (see session-auth-http.js).
      // Note: We also have saga middleware, but nearly no Saga re-throws the error, so it rarely triggers there.
      if (shouldRedirectToLoginForResponse(config, status)) {
        redirectToLoginOnce(status);
      }
    }

    if ((!statusString || statusString[0] === '5') && !ignoreInternalError) {
      hasInternalError = true;
      failedRequests.push({
        apiEndpoint: url,
        statusCode: statusString ? parseInt(statusString, 10) : null,
        method: method?.toUpperCase(),
      });
    }

    return Promise.reject(error);
  }
);

export function getLastErrorStatus() {
  return hasInternalError;
}

export function getFailedRequests() {
  return [...failedRequests];
}

export function getLastFailedRequest() {
  return failedRequests.length ? failedRequests[failedRequests.length - 1] : null;
}
